"use client"

import { useEffect, type RefObject } from "react"

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",")

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.getAttribute("aria-hidden") !== "true",
  )
}

function isolateBackground(
  container: HTMLElement,
  keepInteractive: HTMLElement[] = [],
): () => void {
  const snapshots: Array<{
    element: HTMLElement
    inert: boolean
    ariaHidden: string | null
  }> = []
  let branch: HTMLElement = container

  while (branch.parentElement && branch.parentElement !== document.body) {
    const parent = branch.parentElement
    for (const sibling of Array.from(parent.children)) {
      if (sibling === branch || !(sibling instanceof HTMLElement)) continue
      // Leave explicitly-excluded subtrees interactive (e.g. a header bar that
      // hosts the drawer's own open/close toggle). The trapped focus scope still
      // keeps keyboard Tab inside the dialog; Escape remains the keyboard close.
      if (keepInteractive.some((el) => sibling === el || sibling.contains(el))) continue
      snapshots.push({
        element: sibling,
        inert: sibling.inert,
        ariaHidden: sibling.getAttribute("aria-hidden"),
      })
      sibling.inert = true
      sibling.setAttribute("aria-hidden", "true")
    }
    branch = parent
  }

  return () => {
    for (const snapshot of snapshots) {
      snapshot.element.inert = snapshot.inert
      if (snapshot.ariaHidden === null) {
        snapshot.element.removeAttribute("aria-hidden")
      } else {
        snapshot.element.setAttribute("aria-hidden", snapshot.ariaHidden)
      }
    }
  }
}

interface ModalFocusOptions {
  isOpen: boolean
  containerRef: RefObject<HTMLElement | null>
  initialFocusRef?: RefObject<HTMLElement | null>
  /**
   * Background subtrees to leave interactive instead of inerting. Pass a stable
   * (memoized) array — it's an effect dependency. Use when a control that must
   * keep working while the modal is open lives outside the container (e.g. a
   * sticky header hosting the drawer's own toggle/close button).
   */
  keepInteractive?: Array<RefObject<HTMLElement | null>>
  onEscape?: () => void
  isolate?: boolean
  trapFocus?: boolean
  restoreFocus?: boolean
}

/** Focuses, traps, and optionally isolates a custom modal surface. */
export function useModalFocusManagement({
  isOpen,
  containerRef,
  initialFocusRef,
  keepInteractive,
  onEscape,
  isolate = true,
  trapFocus = true,
  restoreFocus = true,
}: ModalFocusOptions) {
  useEffect(() => {
    if (!isOpen) return
    const container = containerRef.current
    if (!container) return

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
    const keepEls = (keepInteractive ?? [])
      .map((ref) => ref.current)
      .filter((el): el is HTMLElement => el instanceof HTMLElement)
    const restoreBackground = isolate ? isolateBackground(container, keepEls) : () => {}

    const focusFrame = requestAnimationFrame(() => {
      const initialTarget = initialFocusRef?.current ?? getFocusableElements(container)[0] ?? container
      initialTarget.focus({ preventScroll: true })
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && onEscape) {
        event.preventDefault()
        onEscape()
        return
      }
      if (event.key !== "Tab" || !trapFocus) return

      const focusable = getFocusableElements(container)
      if (focusable.length === 0) {
        event.preventDefault()
        container.focus({ preventScroll: true })
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (event.shiftKey && (active === first || !container.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || !container.contains(active))) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      cancelAnimationFrame(focusFrame)
      document.removeEventListener("keydown", handleKeyDown)
      restoreBackground()
      if (restoreFocus && previouslyFocused?.isConnected) {
        requestAnimationFrame(() => previouslyFocused.focus({ preventScroll: true }))
      }
    }
  }, [containerRef, initialFocusRef, keepInteractive, isOpen, isolate, onEscape, restoreFocus, trapFocus])
}
